import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, Instagram } from "lucide-react";
import { SOCIALS } from "@/lib/social";
import { FOLLOW, Signup } from "../../ConceptBits";
import { BRANDS } from "../../concept-content";
import { Blue2Page, Part } from "../Page";
import { CLUB, Draw, EASE, HEART, Head, Ink, PAGES, VIEW } from "../bits";
import { LiveDates } from "../LiveDates";
import "../SectionsA.css";
import "../SectionsB.css";
import "./About.css";

/* About Chelsea, in the blue-2 style. Every line of copy comes from the
   current site (AboutSection, ChelseaIntroSection, CommunitySection,
   ContentSection, /partnerships); spots that need Chelsea's own words are
   marked TODO(content). */

const TAGS = ["Sewing Enthusiast", "Pattern Lover", "ADHD Creative", "Community Builder"];

function Header() {
  return <div className="ba-head">
    <div className="ba-head-copy">
      <motion.p className="ba-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Sewist · Creator · Community Builder</motion.p>
      <h1 className="ba-title">
        <motion.span className="ba-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>nice to</motion.span>
        <span className="ba-title-word" aria-label="meet you">
          {"meet you".split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.04, duration: 0.6, ease: EASE }}>{c === " " ? " " : c}</motion.span>)}
        </span>
      </h1>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.5, ease: EASE }}>
        <p className="ba-motto">Loose Threads, Big Ideas
          <svg viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Draw d="M4 12 C 80 6, 200 6, 296 9" delay={1.1} width={5} /></svg>
        </p>
        <p className="ba-intro">Tying the threads of creativity together. Welcome to my little corner of the internet where sewing meets community and good vibes flow freely.</p>
        <div className="ba-actions">
          <a href="#story" className="b2-btn b2-btn-yellow">Read my story <ArrowRight size={16} aria-hidden="true" /></a>
          <Link to={PAGES.videos} className="b2-btn b2-btn-ghost">Watch my videos</Link>
        </div>
      </motion.div>
    </div>

    <div className="ba-head-photos">
      <motion.figure className="ba-pol ba-pol-main" initial={{ opacity: 0, y: 30, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ delay: 0.3, type: "spring", stiffness: 130, damping: 15 }}>
        <i className="b2-snap-tape b2-ktape-strong" aria-hidden="true" />
        <span><img src="/img/chelsea.jpeg" alt="Chelsea smiling, with long dark hair and a sweater covered in black bows" /></span>
        <figcaption>Hi! That’s me. <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>
      <motion.figure className="ba-pol ba-pol-side" initial={{ opacity: 0, y: 30, rotate: -14 }} animate={{ opacity: 1, y: 0, rotate: -7 }} transition={{ delay: 0.55, type: "spring", stiffness: 130, damping: 15 }}>
        <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
        <span><img src="/chelsea.JPG" alt="Chelsea wearing her handmade cereal-print outfit and a colorful quilted bag" /></span>
        <figcaption>I made the outfit <u>and</u> the bag.</figcaption>
      </motion.figure>
      <svg className="ba-head-heart" viewBox="0 0 30 46" aria-hidden="true"><Draw d={HEART} delay={1.4} width={3.4} /></svg>
    </div>
  </div>;
}

const SCRAPS = [
  { src: "/chelsea-and-cosmo.jpg", alt: "Cosmo the cat on Chelsea’s lap at the sewing machine", caption: <>Cosmo, chief quality<br />inspector</>, r: -5, tape: "pop", pos: "40% 55%" },
  { src: "/chelsea-rainbow-portrait.jpg", alt: "Chelsea smiling in her straw hat in front of a wall of colorful fabric", caption: <>Same me.<br />More sewing.</>, r: 4, tape: "soft", pos: "58% 26%" },
] as const;

function Story() {
  return <Part p="making">
    <section id="story" className="b2-sec ba-story" aria-labelledby="ba-story-title">
      <div className="b2-wrap ba-story-grid">
        <div className="ba-story-copy">
          <p className="b2-kicker">My story</p>
          <Head id="ba-story-title" hand="a girl who" word="makes" tone="blue" />
          <p className="ba-big">Hi! I’m Chelsea — a sewist, crafter, and content creator who believes life is better when you’re making something.</p>
          <p className="b2-lede">I started A Few Loose Threads to build a space where creativity flows freely — without judgment or pressure.</p>
          <p className="b2-lede">Whether I’m sewing a wild patterned outfit, testing the latest craft trend, or sharing my favorite finds — my goal is always the same: to connect, inspire, and support a community of passionate makers.</p>
          {/* TODO(content): Chelsea's own "how I started sewing" story — when she started, who taught her, the first thing she made. */}
          <motion.ul className="ba-tags" initial="off" whileInView="on" viewport={VIEW}>
            {TAGS.map((t, i) => <motion.li key={t} variants={{ off: { opacity: 0, y: 12, rotate: 0 }, on: { opacity: 1, y: 0, rotate: [-3, 2, -1.5, 2.5][i], transition: { delay: 0.2 + i * 0.1, type: "spring", stiffness: 200, damping: 14 } } }}>{t}</motion.li>)}
          </motion.ul>
        </div>

        <ul className="ba-scraps" aria-label="A few snapshots">
          {SCRAPS.map((s, i) => <motion.li key={i} initial={{ opacity: 0, y: 50, rotate: s.r + 8 }} whileInView={{ opacity: 1, y: 0, rotate: s.r }} whileHover={{ y: -8, rotate: 0, zIndex: 5 }} viewport={VIEW}
            transition={{ delay: i * 0.12, type: "spring", stiffness: 130, damping: 15 }}>
            <i className={`b2-snap-tape b2-ktape-${s.tape}`} aria-hidden="true" />
            <span><img src={s.src} alt={s.alt} style={{ objectPosition: s.pos }} /></span>
            <b>{s.caption}</b>
          </motion.li>)}
          <li className="ba-scraps-note" aria-hidden="true">
            <svg className="b2-arrow" viewBox="0 0 80 60"><Ink d="M8 52 C 30 50, 52 38, 62 10" delay={0.8} /><Ink d="M48 20 L63 8 L70 26" delay={1.1} /></svg>
            <span>Cosmo approves<br />every video.</span>
          </li>
        </ul>
      </div>
    </section>
  </Part>;
}

const MAKES = [
  { title: "Sewing", note: "Wild patterned outfits, like the cereal-box print set I made from scratch (matching top and pants!).", to: PAGES.videos, cta: "See my makes", tone: "pop", tape: "extra" },
  { title: "Crafting", note: "Testing the latest craft trend, and bringing home way too much from quilt shows.", to: PAGES.videos, cta: "Watch the hauls", tone: "card", tape: "pop" },
  { title: "Content", note: "Behind the scenes: what a day looks like when I’m filming, sewing, and creating.", to: PAGES.videos, cta: "All my videos", tone: "extra", tape: "soft" },
  { title: "Favorites", note: "The tools, fabrics, and supplies I genuinely use and love for every project.", to: PAGES.favorites, cta: "My favorites", tone: "card", tape: "strong" },
] as const;

function Makes() {
  return <Part p="words">
    <section className="b2-sec ba-makes" aria-labelledby="ba-makes-title">
      <div className="b2-wrap">
        <Head id="ba-makes-title" hand="what I" word="make" tone="blue" className="b2-head-center" />
        <p className="ba-makes-lede">I create content across a few of my favorite worlds — here’s what you can expect.</p>
        <div className="ba-notes">
          {MAKES.map((m, i) => <motion.div key={m.title} className={`ba-note ba-note-${m.tone}`}
            initial={{ opacity: 0, y: 40, rotate: [-3, 2, -1.5, 2.5][i] + 6 }} whileInView={{ opacity: 1, y: 0, rotate: [-3, 2, -1.5, 2.5][i] }} whileHover={{ rotate: 0, y: -6 }} viewport={VIEW}
            transition={{ delay: i * 0.1, type: "spring", stiffness: 130, damping: 15 }}>
            <i className={`b2-snap-tape b2-ktape-${m.tape}`} aria-hidden="true" />
            <h3>{m.title}</h3>
            <p>{m.note}</p>
            <Link to={m.to}>{m.cta} <ArrowRight size={15} aria-hidden="true" /></Link>
          </motion.div>)}
        </div>
      </div>
    </section>
  </Part>;
}

function Club() {
  return <Part p="club">
    <section className="b2-sec ba-club" aria-labelledby="ba-club-title">
      <div className="b2-wrap ba-club-grid">
        <div>
          <p className="b2-kicker">Loose Threads Club · my Patreon</p>
          <Head id="ba-club-title" hand="come sew" word="with me" tone="pink" />
          <p className="b2-lede">Whether you’re joining from your sewing room or across the country, I built Loose Threads Club as a place to create, connect, and grow together.</p>
          <p className="b2-lede">With sew-alongs, member discounts, and a virtual space full of makers who just get it, you’ll always have a project, and people, to come back to.</p>
          <div className="ba-club-actions">
            <Link to={CLUB} className="b2-btn b2-btn-yellow b2-btn-big">See what’s inside <ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
        </div>
        <LiveDates />
      </div>
    </section>
  </Part>;
}

function WorkWithMe() {
  return <Part p="brands">
    <section className="b2-sec ba-work" aria-labelledby="ba-work-title">
      <div className="b2-wrap ba-work-grid">
        <div>
          <Head id="ba-work-title" hand="want to" word="team up?" tone="blue" className="ba-head-sm" />
          <p className="b2-lede">Interested in a brand collaboration? Reach out through Instagram to start the conversation.</p>
          {/* TODO(content): what Chelsea offers brands (videos, retreats, events) and a media kit or email for partnerships. */}
          <a href={SOCIALS.instagram.url} target="_blank" rel="noreferrer" className="b2-btn b2-btn-blue ba-work-cta"><Instagram size={18} aria-hidden="true" /> Message {SOCIALS.instagram.handle}</a>
        </div>
        <div className="ba-logos">
          {/* TODO(content): confirm this list; these are the sponsors of Stitches Night Out. */}
          <p className="b2-note">friends who’ve sponsored my events</p>
          <ul>{BRANDS.map(b => <li key={b.name}><img src={b.src} alt={b.name} /></li>)}</ul>
        </div>
      </div>
    </section>
  </Part>;
}

function Follow() {
  return <Part p="loop">
    <section className="b2-sec ba-follow" aria-labelledby="ba-follow-title">
      <div className="b2-wrap">
        <Head id="ba-follow-title" hand="come say hi on" word="socials" tone="blue" className="b2-head-center ba-head-sm" />
        <ul className="ba-socials">
          {FOLLOW.map((f, i) => <motion.li key={f.s.key} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0, rotate: [-2, 1.5, -1, 2][i] }} viewport={VIEW} transition={{ delay: i * 0.08, duration: 0.5, ease: EASE }}>
            <a href={f.s.url} target="_blank" rel="noreferrer"><span className="ba-social-icon">{f.icon}</span><b>{f.s.platform}</b><small>{f.s.handle}</small></a>
          </motion.li>)}
        </ul>
        <div className="ba-letter">
          <p className="b2-note">or get one email a week, mostly fabric:</p>
          <Signup source="about-blue-2" className="b2-signup" button={<>Count me in <ArrowRight size={18} aria-hidden="true" /></>} done={<>You’re in! Watch your inbox <span aria-hidden="true">♡</span></>} />
        </div>
      </div>
    </section>
  </Part>;
}

export function Blue2About() {
  return <Blue2Page current="about" tone="coral" header={<Header />}>
    <Story />
    <Makes />
    <Club />
    <WorkWithMe />
    <Follow />
  </Blue2Page>;
}
